import { Component, Input } from '@angular/core';
import { Trailers, Trucks, Drivers } from '../../interfaces';
import { DataServiceService } from '../../services/data-service.service';
import { ConfirmationService, MessageService } from 'primeng/api';

export type EquipmentType = 'truck' | 'trailer';

export interface EquipmentAssignmentResult {
  success: boolean;
  message: string;
  equipmentType: EquipmentType;
}

@Component({
  selector: 'app-assign-equipment',
  templateUrl: './assign-equipment.component.html',
  styleUrl: './assign-equipment.component.scss'
})
export class AssignEquipmentComponent {
  
  @Input() trucks: Trucks[] = [];
  @Input() trailers: Trailers[] = [];
  @Input() drivers: Drivers[] = [];

  constructor(
    private dataServiceService: DataServiceService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  /**
   * Generic equipment assignment handler that checks for conflicts and handles reassignment
   * @param targetDriver - The driver to assign equipment to
   * @param equipment - The truck or trailer to assign
   * @param equipmentType - Type of equipment ('truck' or 'trailer')
   * @param onSuccess - Optional callback for successful assignment
   * @param onError - Optional callback for errors
   */
  assignEquipmentToDriver(
    targetDriver: Drivers,
    equipment: Trucks | Trailers,
    equipmentType: EquipmentType,
    onSuccess?: (result: EquipmentAssignmentResult) => void,
    onError?: (error: any) => void
  ): void {
    if (!equipment || !targetDriver) {
      this.showErrorMessage('Invalid equipment or driver selected');
      return;
    }

    // Check for existing assignment
    const existingAssignment = this.findExistingAssignment(equipment, equipmentType, targetDriver);

    if (existingAssignment) {
      this.handleReassignmentConfirmation(
        targetDriver,
        equipment,
        equipmentType,
        existingAssignment,
        onSuccess,
        onError
      );
    } else {
      // No conflict, proceed with direct assignment
      this.performEquipmentAssignment(targetDriver, equipment, equipmentType, onSuccess, onError);
    }
  }

  /**
   * Find if equipment is already assigned to another driver
   */
  private findExistingAssignment(
    equipment: Trucks | Trailers,
    equipmentType: EquipmentType,
    targetDriver: Drivers
  ): Drivers | null {
    return this.drivers.find(driver => {
      if (driver.driver_id === targetDriver.driver_id) return false;

      if (equipmentType === 'truck') {
        const truck = equipment as Trucks;
        const matchesById = driver.truck_id != null && driver.truck_id === truck.truck_id;
        const matchesByNumber = driver.truck_number && driver.truck_number === truck.truck_number;
        return matchesById || matchesByNumber;
      } else {
        const trailer = equipment as Trailers;
        return driver.trailer_id != null && driver.trailer_id === trailer.trailer_id;
      }
    }) || null;
  }

  /**
   * Show confirmation dialog for reassignment
   */
  private handleReassignmentConfirmation(
    targetDriver: Drivers,
    equipment: Trucks | Trailers,
    equipmentType: EquipmentType,
    existingAssignment: Drivers,
    onSuccess?: (result: EquipmentAssignmentResult) => void,
    onError?: (error: any) => void
  ): void {
    const equipmentName = equipmentType === 'truck' 
      ? (equipment as Trucks).truck_number 
      : (equipment as Trailers).trailer_number;

    this.confirmationService.confirm({
      message: `This ${equipmentType} (${equipmentName}) is currently assigned to ${existingAssignment.first_name} ${existingAssignment.last_name}. Would you like to reassign it?`,
      header: 'Reassign Equipment',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        this.performReassignment(targetDriver, equipment, equipmentType, existingAssignment, onSuccess, onError);
      },
      reject: () => {
        // User cancelled - could call onError if needed
        if (onError) {
          onError(new Error('Assignment cancelled by user'));
        }
      }
    });
  }

  /**
   * Perform the actual reassignment (remove from old driver, assign to new)
   */
  private performReassignment(
    targetDriver: Drivers,
    equipment: Trucks | Trailers,
    equipmentType: EquipmentType,
    existingAssignment: Drivers,
    onSuccess?: (result: EquipmentAssignmentResult) => void,
    onError?: (error: any) => void
  ): void {
    // First remove equipment from existing driver
    const updatedExistingDriver = { ...existingAssignment };
    
    if (equipmentType === 'truck') {
      updatedExistingDriver.truck_id = null;
      updatedExistingDriver.truck_number = null;
    } else {
      updatedExistingDriver.trailer_id = null;
    }

    this.dataServiceService.updateDriverDetails(existingAssignment.driver_id, updatedExistingDriver)
      .subscribe({
        next: () => {
          // Now assign to new driver
          this.performEquipmentAssignment(targetDriver, equipment, equipmentType, onSuccess, onError);
        },
        error: (error) => {
          this.showErrorMessage(`Failed to remove ${equipmentType} from previous driver`);
          if (onError) onError(error);
        }
      });
  }

  /**
   * Perform the actual equipment assignment
   */
  private performEquipmentAssignment(
    targetDriver: Drivers,
    equipment: Trucks | Trailers,
    equipmentType: EquipmentType,
    onSuccess?: (result: EquipmentAssignmentResult) => void,
    onError?: (error: any) => void
  ): void {
    const updatedDriver = { ...targetDriver };

    if (equipmentType === 'truck') {
      const truck = equipment as Trucks;
      updatedDriver.truck_id = truck.truck_id || null;
      updatedDriver.truck_number = truck.truck_number;
    } else {
      const trailer = equipment as Trailers;
      updatedDriver.trailer_id = trailer.trailer_id;
    }

    this.dataServiceService.updateDriverDetails(targetDriver.driver_id, updatedDriver)
      .subscribe({
        next: () => {
          const result: EquipmentAssignmentResult = {
            success: true,
            message: `${equipmentType.charAt(0).toUpperCase() + equipmentType.slice(1)} assigned successfully`,
            equipmentType
          };
          
          this.showSuccessMessage(result.message);
          if (onSuccess) onSuccess(result);
        },
        error: (error) => {
          this.showErrorMessage(`Failed to assign ${equipmentType}`);
          if (onError) onError(error);
        }
      });
  }

  /**
   * Remove equipment from driver
   */
  removeEquipmentFromDriver(
    driver: Drivers,
    equipmentType: EquipmentType,
    onSuccess?: (result: EquipmentAssignmentResult) => void,
    onError?: (error: any) => void
  ): void {
    const updatedDriver = { ...driver };

    if (equipmentType === 'truck') {
      updatedDriver.truck_id = null;
      updatedDriver.truck_number = null;
    } else {
      updatedDriver.trailer_id = null;
    }

    this.dataServiceService.updateDriverDetails(driver.driver_id, updatedDriver)
      .subscribe({
        next: () => {
          const result: EquipmentAssignmentResult = {
            success: true,
            message: `${equipmentType.charAt(0).toUpperCase() + equipmentType.slice(1)} removed successfully`,
            equipmentType
          };
          
          this.showSuccessMessage(result.message);
          if (onSuccess) onSuccess(result);
        },
        error: (error) => {
          this.showErrorMessage(`Failed to remove ${equipmentType}`);
          if (onError) onError(error);
        }
      });
  }

  // Convenience methods for specific equipment types
  assignTruckToDriver(driver: Drivers, truck: Trucks, onSuccess?: (result: EquipmentAssignmentResult) => void, onError?: (error: any) => void): void {
    this.assignEquipmentToDriver(driver, truck, 'truck', onSuccess, onError);
  }

  assignTrailerToDriver(driver: Drivers, trailer: Trailers, onSuccess?: (result: EquipmentAssignmentResult) => void, onError?: (error: any) => void): void {
    this.assignEquipmentToDriver(driver, trailer, 'trailer', onSuccess, onError);
  }

  // Helper methods for UI feedback
  private showSuccessMessage(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showErrorMessage(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }
}